import React from 'react';
import { useQuery } from "react-query";
import Club from "../Club";
import CloseIcon from "../../../shared/svg/CloseIcon";
import PlayCircleIcon from "../../../shared/svg/PlayCircleIcon";
import TicketIcon from "../../../shared/svg/TicketIcon";
import ToggleButton from "../../../shared/components/ToggleButton";
import { statusTypes } from "../../utils";
import { useReactAppState } from "../../../shared/containers/utils";
import { composeGeoLocatedProviders, countryTypes, highlightTypes, matchHighlightsTagSlug } from "../../../shared/utils";
import { modalActionTypes, modalTypes } from "../../../shared/hooks/useModalOptions";
import VideoModal from "../../../shared/components/VideoModal";
import { trackMatchHubClick } from '../../../../libraries/_modules/tracking/helpers';
import { fetchClient } from "../../../shared/api/fetching";
import { apiURL, forgeMatchContentVideosAPI, forgeMatchVideosAPI } from "../../../shared/api/variables";
import { composeVideoList } from "../../../mls-match-feed/utils";

const MatchModal = ({ queryConfig, matchSportecId, closeModal, slug, status, competition, homeClubData, awayClubData, matchDateTime, venue, listType, promoInfo, mediaProviders, winnerStatus, isTimeTbd }) => {
  const { t, location, updateModalOptions, videoTags } = useReactAppState();

  const forgeMatchVideosEndpoint = forgeMatchVideosAPI({ matchSportecId });
  const forgeMatchContentVideosEndpoint = forgeMatchContentVideosAPI({ matchSlug: slug });

  const { data: forgeMatchVideos } = useQuery({
    queryKey: ["forge-match-videos", matchSportecId],
    queryFn: () => fetchClient({ apiURL: apiURL.forgeDAPI, endpoint: forgeMatchVideosEndpoint }),
    ...queryConfig,
    select: videos => videos?.items
  });

  const { data: forgeMatchContentVideos } = useQuery({
    queryKey: ["forge-match-content-videos", slug],
    queryFn: () => fetchClient({ apiURL: apiURL.forgeDAPI, endpoint: forgeMatchContentVideosEndpoint }),
    ...queryConfig,
    select: videos => videos?.items
  });

  const videoList = React.useMemo(() => {
    const matchVideos = forgeMatchVideos?.length >= 0 && forgeMatchContentVideos?.length >= 0 ? [...forgeMatchVideos, ...forgeMatchContentVideos] : [];
    return composeVideoList(matchVideos, videoTags);
  }, [forgeMatchVideos, forgeMatchContentVideos]);

  const { country } = location || {};
  const { streaming, national, radio } = composeGeoLocatedProviders(mediaProviders, country);

  const { homeLinear, awayLinear, homeStreaming, awayStreaming, clubLinear, clubStreaming, clubRadio } = mediaProviders || {};

  const isNorthAmerica = country === countryTypes.canada || country === countryTypes.unitedStates;

  const mergedProviders = [...national, ...streaming, ...radio, ...(homeLinear != null ? homeLinear : []), ...(awayLinear != null ? awayLinear : []), ...(homeStreaming != null ? homeStreaming : []), ...(awayStreaming != null ? awayStreaming : []), ...(clubLinear != null ? clubLinear : []), ...(clubStreaming != null ? clubStreaming : []), ...(clubRadio != null ? clubRadio : [])];
  const mergedStreaming = [...(clubStreaming != null ? clubStreaming : []), ...streaming, ...(homeStreaming != null ? homeStreaming : []), ...(awayStreaming != null ? awayStreaming : [])];

  const { firstPartyTickets, thirdPartyTickets } = promoInfo || {};
  const { fullDate, matchFullDateTime } = matchDateTime || {};

  const { name, city } = venue || {};
  const displayVenueLocation = name != null && city != null
    ? `${name}, ${city}`
    : name != null
      ? name
      : city != null
        ? city
        : null;

  const {
    isHomeWinner,
    isAwayWinner
  } = winnerStatus || {};

  const title = status?.abbreviation === statusTypes.live
    ? t("match_watch_live")
    : status?.abbreviation === statusTypes.pre
      ? t("ticketing_buy_tickets")
      : t("match_replay");

  const toggleDialog = (currentVideo) => {
    const foundVideoIndex = videoList.findIndex((video) => {
      return currentVideo._entityId === video._entityId;
    });

    const videos = [
      currentVideo, ...videoList.slice(0, foundVideoIndex),
      ...videoList.slice(foundVideoIndex + 1)
    ];

    updateModalOptions({ type: modalActionTypes.modalClassName, payload: "mls-o-modal mls-o-modal--dark mls-o-modal--video-playlist" });
    updateModalOptions({ type: modalActionTypes.modalType, payload: modalTypes.default });
    updateModalOptions({ type: modalActionTypes.label, payload: t("match_highlights") });
    updateModalOptions({ type: modalActionTypes.component, payload: VideoModal });
    updateModalOptions({ type: modalActionTypes.modalData, payload: { videoList: videos, video: currentVideo } });
  };

  const gameHighlights = videoList.find(video => video?.fields?.highlightType === highlightTypes.gameHighlights || video?.tags.find(tag => tag?.slug === matchHighlightsTagSlug));
  const fullGame = videoList.find(video => video?.fields?.highlightType === highlightTypes.fullGame);
  const condensedGame = videoList.find(video => video?.fields?.highlightType === highlightTypes.condensedGame);

  const handleClick = (clickItem) => () => {
    trackMatchHubClick({
      clickType: 'match modal button',
      clickItem,
      match: slug,
      matchState: status?.abbreviation,
      competition: competition.shortName,
    });
  };

  if (!status) {
    return null;
  }

  return (
    <div className="mls-o-modal mls-o-modal--match mls-o-match-strip">
      <button onClick={closeModal} className="mls-o-buttons__slideshow">
        <CloseIcon size="xx-small" />
      </button>
      <div className="mls-o-modal__title">{title}</div>
      <div className="mls-o-modal__club-group">
        <Club clubData={homeClubData} isWinner={isHomeWinner} isHome showShortName />
        <div className="mls-o-modal__versus">{t("match_vs")}</div>
        <Club clubData={awayClubData} isWinner={isAwayWinner} isHome={false} showShortName />
      </div>
      {
        status?.abbreviation === statusTypes.live
          ? (
            <ul className="mls-o-match-strip__tv-streaming-provider-list">
              {
                mergedProviders && mergedProviders.length > 0
                  ? mergedProviders.map((provider, index) => {
                    const { broadcasterName, broadcasterTypeLabel } = provider || {};

                    return (
                      broadcasterName != null && broadcasterTypeLabel != null
                        ? (
                          <li key={`${broadcasterName}-${index}`} className="mls-o-match-strip__tv-streaming-provider-item">
                            <span className="mls-o-match-strip__tv-streaming-provider-label">{broadcasterTypeLabel}</span>
                            <span className="mls-o-match-strip__tv-streaming-provider">{broadcasterName}</span>
                          </li>
                        ) : null
                    );
                  })
                  : null
              }
            </ul>
          )
          : (
            <div className="mls-o-modal__match-info">
              {isTimeTbd ? <div className="mls-o-modal__date-time">{fullDate ? `${fullDate}, ` : ""}TBD</div>
                :
                matchFullDateTime ? (
                  <div className="mls-o-modal__date-time">
                    {matchFullDateTime}
                  </div>
                ) : null
              }
              {
                displayVenueLocation
                  ? <div className="mls-o-modal__venue">{displayVenueLocation}</div>
                  : null
              }
            </div>
          )
      }
      <div className="mls-o-modal__cta-group">
        {
          status?.abbreviation === statusTypes.live && isNorthAmerica
            ? (
              mergedStreaming && mergedStreaming.length > 0
                ? mergedStreaming.map((item, index) => {
                  const { broadcasterName, broadcasterStreamingURL, broadcasterShortName } = item || {};
                  return (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mls-o-modal__cta"
                      key={`${broadcasterName}-${index}`}
                      onClick={handleClick("watch live")}
                      href={broadcasterStreamingURL}>
                      <PlayCircleIcon extraClasses={"mls-o-modal__play-circle"} />
                      <span className="mls-o-modal__cta-label">{`${t("match_watch_live_on")} ${broadcasterShortName ? broadcasterShortName : broadcasterName}`}</span>
                    </a>
                  );
                })
                : null
            )
            : status?.abbreviation === statusTypes.pre
              ? (
                <>
                  {
                    firstPartyTickets?.url
                      ? (
                        <ToggleButton
                          target={`${firstPartyTickets?.openInNewTab ? "_blank" : "_self"}`}
                          rel={`${firstPartyTickets?.openInNewTab ? "noopener noreferrer" : ""}`}
                          alt={`${firstPartyTickets?.accessibleText ? firstPartyTickets.accessibleText : ""}`}
                          href={firstPartyTickets.url}
                          onClick={handleClick("buy from club")}
                          // className={`mls-o-modal__cta ${firstPartyTickets.url.includes("seatgeek") ? "mls-o-modal__cta--third" : "mls-o-modal__cta--first"}`}>
                          className="mls-o-modal__cta mls-o-modal__cta--first">
                          {
                            // firstPartyTickets.url.includes("seatgeek")
                            // ? <SeatGeekIcon extraClasses={"mls-o-modal__third-party"} />
                            <TicketIcon extraClasses={"mls-o-modal__tickets mls-o-modal__tickets"} />
                          }
                          <span className="mls-o-modal__cta-label">{firstPartyTickets?.displayText ? firstPartyTickets.displayText : t("buy_from_club")}</span>
                        </ToggleButton>
                      )
                      : null
                  }
                  {
                    thirdPartyTickets?.url
                      ? (
                        <ToggleButton
                          target={`${thirdPartyTickets?.openInNewTab ? "_blank" : "_self"}`}
                          rel={`${thirdPartyTickets?.openInNewTab ? "noopener noreferrer" : ""}`}
                          alt={`${thirdPartyTickets?.accessibleText != null ? thirdPartyTickets.accessibleText : ""}`}
                          href={thirdPartyTickets.url}
                          onClick={handleClick("buy from fans")}
                          // className={`mls-o-modal__cta ${thirdPartyTickets.url.includes("seatgeek") ? "mls-o-modal__cta--third" : "mls-o-modal__cta--first"}`}>
                          className="mls-o-modal__cta mls-o-modal__cta--first">
                          {
                            // thirdPartyTickets.url.includes("seatgeek")
                            // ? <SeatGeekIcon extraClasses={"mls-o-modal__third-party"} />
                            <TicketIcon extraClasses={"mls-o-modal__tickets mls-o-modal__tickets"} />
                          }
                          <span className="mls-o-modal__cta-label">{thirdPartyTickets?.displayText ? thirdPartyTickets.displayText : t("buy_from_fans")}</span>
                        </ToggleButton>
                      )
                      : null
                  }
                </>
              )
              : videoList && videoList.length > 0
                ? (
                  <>
                    {
                      fullGame != null
                        ? (
                          <ToggleButton
                            className="mls-o-modal__cta"
                            onClick={() => toggleDialog(fullGame)}>
                            <PlayCircleIcon extraClasses={"mls-o-modal__play-circle"} />
                            <span className="mls-o-modal__cta-label">{t("match_full_replay")}</span>
                          </ToggleButton>
                        ) : null
                    }
                    {
                      condensedGame != null
                        ? (
                          <ToggleButton
                            className="mls-o-modal__cta"
                            onClick={() => toggleDialog(condensedGame)}>
                            <PlayCircleIcon extraClasses={"mls-o-modal__play-circle"} />
                            <span className="mls-o-modal__cta-label">{t("match_mls_fifteen")}</span>
                          </ToggleButton>
                        ) : null
                    }
                    {
                      gameHighlights != null
                        ? (
                          <ToggleButton
                            className="mls-o-modal__cta"
                            onClick={() => toggleDialog(gameHighlights)}>
                            <PlayCircleIcon extraClasses={"mls-o-modal__play-circle"} />
                            <span className="mls-o-modal__cta-label">{t("match_highlights")}</span>
                          </ToggleButton>
                        ) : null
                    }
                  </>
                ) : null
        }
      </div>
    </div>
  );
};

export default MatchModal;
